import { Box, Text, styled, alpha } from '@ltvco/refresh-lib/theme';

import {
  IconWrapper,
  getTableCellPrimaryStyles,
  getTableCellSecondaryStyles,
} from '../../../shared/components';

// Re-export IconWrapper as SortIcon
export const SortIcon = styled(IconWrapper)({});
SortIcon.defaultProps = { size: 16 };

export const DocketEntriesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

export const TableHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  backgroundColor: alpha(theme.palette.text.secondary, 0.12),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 1.5),
  },
}));

export const HeaderCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
  justifyContent: 'center',
}));

export const HeaderCellText = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    lineHeight: '14px',
  },
}));

export const HeaderCellDescription = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
  flex: 1,
}));

export const Divider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.divider,
}));

export const TableRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAlternate',
})<{ isAlternate?: boolean }>(({ theme, isAlternate }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  minHeight: '46px',
  backgroundColor: isAlternate
    ? theme.palette.grey[50]
    : theme.palette.common.white,
  borderRadius: '12px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 1.5),
    minHeight: 'auto',
    alignItems: 'flex-start',
  },
}));

export const DocketNumberCell = styled(Text)(({ theme }) => ({
  ...getTableCellSecondaryStyles(theme),
  textAlign: 'center',
  width: '70px',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '70px',
    fontSize: '14px',
    lineHeight: '16px',
  },
}));

export const DateCell = styled(Text)(({ theme }) => ({
  ...getTableCellSecondaryStyles(theme),
  textAlign: 'center',
  width: '70px',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '70px',
    fontSize: '14px',
    lineHeight: '16px',
  },
}));

export const DescriptionCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

export const DescriptionCellNoEntryNumber = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  '& > div': {
    marginTop: `-${theme.spacing(1.875)}`,
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& > div': {
      marginTop: 0,
    },
  },
}));

export const DescriptionText = styled(Text)(({ theme }) => ({
  ...getTableCellPrimaryStyles(theme),
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  [theme.breakpoints.up('md')]: {
    maxWidth: 'calc(100% - 200px)',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 'calc(100% - 250px)',
  },
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '20px',
    width: '100%',
  },
}));

export const BuyOnPacerButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  height: '28px',
  [theme.breakpoints.down('sm')]: {
    height: '26px',
    gap: theme.spacing(1),
  },
}));

export const BuyOnPacerIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '18px',
  color: theme.palette.primary.main,
  '& svg': {
    width: '16px',
    height: '18px',
    fontSize: '18px',
  },
}));

export const BuyOnPacerText = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '22px',
  color: theme.palette.primary.main,
  textTransform: 'capitalize',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '20px',
  },
}));

export const DocumentItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

export const DocumentItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const DocumentRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  minHeight: '28px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    minHeight: 'auto',
  },
}));

export const DocumentTitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  [theme.breakpoints.up('md')]: {
    maxWidth: 'calc(100% - 180px)',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 'calc(100% - 200px)',
  },
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '100%',
  },
}));

export const DocumentActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  flexShrink: 0,
  minHeight: '28px',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'flex-start',
    minHeight: 'auto',
  },
}));

export const AttachmentRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
}));

export const AttachmentTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  [theme.breakpoints.up('md')]: {
    maxWidth: 'calc(100% - 180px)',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 'calc(100% - 200px)',
  },
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '100%',
  },
}));

export const AttachmentText = styled(Text)(({ theme }) => ({
  ...getTableCellSecondaryStyles(theme),
}));
